import * as React from "react";
import * as ReactDOM from "react-dom";
import {
  DashboardData,
  DynamicFilterChoice,
  DynamicFilterPart,
  FilterPart,
  Part,
  PartState,
  ReportPart,
  SelectedMember,
  TARContainerOrientation,
  TARGraphSerieType,
  TARLegendPosition,
  TARMemberState,
  TARPageSetup,
  TARPresentationType,
  TilePart,
  TMouseButton,
  TPartState,
  UpdateDashboard,
  VisibleMember,
} from "../common/communication.base";
import { NclDashboard } from "../common/components.ncl";
import { AcquireControl, WithContextPlacementProps, StyleHelper } from "./k2hoc";
import { Bar, defaults, Line, Pie } from "react-chartjs-2";
import { ChartOptions, Chart as ChartJS, ChartArea } from "chart.js";
import { K2Img } from "./K2Image";
import { Context } from "../appcontext";
import { K2Action } from "./K2Action";
import { K2RuleWithVCXProps } from "./k2StyleRenderer";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { VCXContext } from "./app";

interface LayoutProps extends K2RuleWithVCXProps {
  direction: "row" | "column";
  basis: number | string;
  isChart?: boolean;
}

const layout = (props: LayoutProps) => ({
  flex: `0 1 ${props.basis}%`,
  flexDirection: props.direction,
  minHeight: "60px",
  small: {
    flexDirection: "column",
    flex: "0 0 auto",
    minHeight: props.isChart ? "200px" : null,
  },
});

const dashboard = () => ({
  flexDirection: "column" as const,
  flex: "1 1 auto",
});

const header = () => ({
  justifyContent: "space-between",
  flex: "0 0 auto",
});

const body = () => ({
  overflow: "auto",
  flex: "1 1 auto",
});

const containers = (props: K2RuleWithVCXProps & { direction: "row" | "column"; justify: string; align: string; menuPartsCount: number }) => ({
  flexDirection: props.direction,
  flex: "1 1 auto",
  gap: "5px",
  justifyContent: props.justify,
  alignItems: props.align,
  minWidth: props.menuPartsCount > 0 ? props.menuPartsCount * 330 + 10 + "px" : null,
  small: {
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "stretch",
    minWidth: 0,
  },
});

const filtersWrap = () => ({
  flex: "1 1 auto",
  small: {
    flexDirection: "column",
  },
});

const tile = (props: K2RuleWithVCXProps & { background: string; isMenu: boolean }) => ({
  flexDirection: "column" as const,
  background: props.background,
  width: props.isMenu ? "330px" : null,
  height: props.isMenu ? "140px" : null,
  small: {
    width: "auto",
    height: "auto",
  },
});

const tileTop = () => ({
  alignItems: "center",
  color: "white",
  flex: "1 1 auto",
  padding: "30px",
  whiteSpace: "normal" as const,
});

const tileBottom = (props: K2RuleWithVCXProps & { background: string }) => ({
  flex: "0 1 40px",
  background: props.background,
  justifyContent: "flex-end",
  color: "white",
  alignItems: "center",
});

const filterName = () => ({
  flex: "0 0 auto",
  alignItems: "center",
});

const filters = () => ({
  alignItems: "center",
  gap: "5px",
  flex: "1 1 auto",
  flexWrap: "wrap" as const,
  overflow: "auto",
});

const modalBg = () => ({
  position: "absolute" as const,
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  background: "white",
  zIndex: 2,
  display: "flex",
  flexDirection: "column" as const,
});

const chartHeader = () => ({
  justifyContent: "space-between",
  flex: "0 0 auto",
});

const noData = () => ({
  justifyContent: "center",
  alignItems: "center",
  flex: "1 1 auto",
  display: "flex",
});

const buttonRule = (props: K2RuleWithVCXProps & { isSelected?: boolean }) => ({
  border: `1px solid ${props.isSelected ? "#67CEF5" : "#ccc"}`,
  padding: "3px",
  font: "inherit",
  background: props.isSelected ? "#67CEF5" : "white",
  color: props.isSelected ? "white" : null,
});

const select = () => ({
  flex: "1 1 auto",
  border: "1px solid #ccc",
  background: "white",
  padding: "2px 5px",
  ":hover": {
    background: "rgba(0, 0, 0, 0.17)",
  },
  ":focus": {
    background: "rgba(0, 0, 0, 0.17)",
  },
  ":active": {
    background: "rgba(0, 84, 166, 0.5)",
  },
});

const selectMembers = () => ({
  flex: "1 1 auto",
  alignItems: "center",
});

const selectBg = () => ({
  position: "absolute" as const,
  top: 0,
  left: 0,
  height: "100%",
  width: "100%",
});

const selectModal = (props: K2RuleWithVCXProps & { top: number; width: number; left: number }) => ({
  position: "relative" as const,
  top: props.top + "px",
  left: props.left + "px",
  background: "white",
  border: "1px solid",
  padding: "10px",
  width: props.width + "px",
  height: "60%",
  flexDirection: "column" as const,
  justifyContent: "space-between",
  zIndex: 1,
});

const DashboardContext = createContext<{
  control: NclDashboard;
  dataVersion: number;
  partClick: (
    part: Part,
    members: SelectedMember[],
    shiftKey: boolean,
    ctrlKey: boolean,
    datasetIndex: number,
    index: number,
    mouse: TMouseButton
  ) => Promise<void>;
}>(null);

export const K2Dashboard = (props: WithContextPlacementProps): JSX.Element => {
  const control = useRef<NclDashboard>(AcquireControl(props.controlUID, props.vrUID, (ctrl) => ctrl instanceof NclDashboard) as NclDashboard);
  const [dashboardData, setDashboardData] = useState<UpdateDashboard>(null);
  const [vcxVersion, setVcxVersion] = useState(-1);
  const context = useContext(VCXContext);

  useEffect(() => {
    defaults.color = "black";
    defaults.font.family = context.vcx.DashboardControl.GraphFont.FontName;
    defaults.font.size = (context.vcx.DashboardControl.GraphFont.FontSize / 3) * 4;

    if (context.vcx.DashboardControl.GraphFont.IsItalic) {
      defaults.font.style = "italic";
    }

    if (context.vcx.DashboardControl.GraphFont.IsBold) {
      defaults.font.weight = "bold";
    }

    setDashboardData(control.current.init({ updateState: updateState, updateVCX: updateVCX }) as UpdateDashboard);
  }, []);

  const updateState = (data: UpdateDashboard) => {
    setDashboardData(data);
  };

  const updateVCX = (vcxVersion: number) => {
    setVcxVersion(vcxVersion);
  };

  const getAligment = (aligment: number) => {
    let justifyContent: string;

    switch (aligment) {
      case 0:
        justifyContent = "flex-start";
        break;
      case 1:
        justifyContent = "center";
        break;
      case 2:
        justifyContent = "flex-end";
        break;
      default:
        break;
    }

    return justifyContent;
  };

  const handlePartClick = async (
    part: Part,
    members: SelectedMember[],
    shiftKey: boolean,
    ctrlKey: boolean,
    datasetIndex: number,
    index: number,
    mouse: TMouseButton
  ) => {
    if (part) {
      if (!control.current.isPartsUpdateCompleted()) return;

      const ids = members?.map((member) => (member ? member.Identifier : null));
      control.current.partClick(part.PartIdentifier, ids ? ids : [], shiftKey, ctrlKey, datasetIndex, index, mouse);
    }
  };

  const handleIsPartChanged = (id: string): boolean => {
    if (control.current) {
      return control.current.isChangedParts(id);
    }

    return false;
  };

  const handleGetPartState = (id: string): PartState => {
    if (control.current) {
      return control.current.getPartState(id);
    }

    return undefined;
  };

  const toggleMember = (partId: string, memberId: string) => {
    control.current.toggleMember(partId, memberId);
  };

  const data: DashboardData = control.current.DashboardData;

  if (!data || Object.keys(data).length === 0) {
    if (dashboardData?.Error)
      return (
        <div
          style={StyleHelper(control.current, { ...props.style })}
          className={
            context.renderer.renderRule(dashboard, { vcx: control.current.VCX }) +
            " " +
            context.renderer.renderFontRule(control.current.VCX.LabelControl.Font, control.current.VCX)
          }
        >
          {control.current.Header?.LQuickButton && (
            <K2Action
              controlUID={control.current.Header?.LQuickButton.MetaData.ControlUID}
              vrUID={control.current.getRealizerUID()}
              style={{ flex: "none", width: "auto", paddingRight: control.current.VCX.cssSizeMap(4, "px") }}
            />
          )}
          <div className={context.renderer.renderRule(noData, { vcx: control.current.VCX })}>{dashboardData.Error}</div>
        </div>
      );

    return null;
  }

  const horizontalAligment = getAligment(data.HorizontalAlignment);
  const verticalAligment = getAligment(data.VerticalAlignment);
  let container: JSX.Element;

  if (data.PageSetup === TARPageSetup.psFiltersAboveContainersBelow) {
    container = <FiltersAboveContainersBelow data={data} isChangedParts={handleIsPartChanged} getPartState={handleGetPartState} toggleMember={toggleMember} />;
  } else {
    container = (
      <ContainersHorizontallyOrVertically data={data} isChangedParts={handleIsPartChanged} getPartState={handleGetPartState} toggleMember={toggleMember} />
    );
  }

  let menuPartsCount = 0;

  data.Containers.map((container) => {
    const isMenu = container.Parts.some((part) => "Description" in part);
    if (isMenu) {
      if (container.Parts.length > menuPartsCount) {
        menuPartsCount = container.Parts.length;
      }
    }
  });

  return (
    <DashboardContext.Provider value={{ control: control.current, dataVersion: (dashboardData as UpdateDashboard)?.DataVersion, partClick: handlePartClick }}>
      <div
        style={StyleHelper(control.current, { ...props.style })}
        className={
          context.renderer.renderRule(dashboard, { vcx: control.current.VCX }) +
          " " +
          context.renderer.renderFontRule(control.current.VCX.LabelControl.Font, control.current.VCX)
        }
      >
        <div className={context.renderer.renderRule(header, { vcx: control.current.VCX })}>
          {control.current.Header?.LQuickButton && (
            <K2Action
              controlUID={control.current.Header?.LQuickButton.MetaData.ControlUID}
              vrUID={control.current.getRealizerUID()}
              style={{ flex: "none", width: "auto", paddingRight: control.current.VCX.cssSizeMap(4, "px") }}
            />
          )}
          <p style={{ textAlign: "center" }} className={context.renderer.renderFontRule(control.current.VCX.ExpanderControl.HeaderFont, control.current.VCX)}>
            {data.Title}
          </p>
          <div></div>
        </div>
        <div className={context.renderer.renderRule(body, { vcx: control.current.VCX })}>
          <div
            className={context.renderer.renderRule(containers, {
              vcx: control.current.VCX,
              direction: data.PageSetup === TARPageSetup.psContainersHorizontally ? "row" : "column",
              justify: horizontalAligment,
              align: verticalAligment,
              menuPartsCount: menuPartsCount,
            })}
          >
            {container}
          </div>
        </div>
      </div>
    </DashboardContext.Provider>
  );
};

interface PartBaseProps {
  isChangedParts: (id: string) => boolean;
  getPartState: (id: string) => PartState;
  toggleMember?: (partId: string, memberId: string) => void;
}

interface ContainerProps extends PartBaseProps {
  data: DashboardData;
}

const FiltersAboveContainersBelow = (props: ContainerProps) => {
  const vcx = useContext(VCXContext);

  return (
    <>
      <div
        style={{ gap: "5px" }}
        className={vcx.renderer.renderRule(layout, { vcx: vcx.vcx, direction: "row", basis: props.data.Containers[0].ContainerWidth })}
      >
        {props.data.Containers[0].Parts.map((part) => {
          return (
            <K2Part
              key={part.PartIdentifier}
              part={part}
              getPartState={props.getPartState}
              isChangedParts={props.isChangedParts}
              toggleMember={props.toggleMember}
            />
          );
        })}
      </div>

      <div style={{ gap: "5px" }} className={vcx.renderer.renderRule(filtersWrap, { vcx: vcx.vcx, direction: "row", basis: null })}>
        {props.data.Containers.map((container, index) => {
          if (index > 0) {
            return (
              <div
                key={index}
                style={{ gap: "5px", minHeight: container.Orientation === TARContainerOrientation.coVertical ? "auto" : null }}
                className={vcx.renderer.renderRule(layout, {
                  vcx: vcx.vcx,
                  direction: container.Orientation === TARContainerOrientation.coHorizontal ? "row" : "column",
                  basis: container.ContainerWidth,
                })}
              >
                {container.Parts.map((part) => {
                  return (
                    <K2Part
                      key={part.PartIdentifier}
                      part={part}
                      getPartState={props.getPartState}
                      isChangedParts={props.isChangedParts}
                      toggleMember={props.toggleMember}
                    />
                  );
                })}
              </div>
            );
          }
        })}
      </div>
    </>
  );
};

const ContainersHorizontallyOrVertically = (props: ContainerProps) => {
  const vcx = useContext(VCXContext);

  return (
    <>
      {props.data.Containers.map((container, index) => {
        return (
          <div
            key={index}
            style={{ gap: "5px", minHeight: container.Orientation === TARContainerOrientation.coVertical ? "auto" : null }}
            className={vcx.renderer.renderRule(layout, {
              vcx: vcx.vcx,
              direction: container.Orientation === TARContainerOrientation.coHorizontal ? "row" : "column",
              basis: "Description" in container.Parts[0] ? "auto" : container.ContainerWidth,
            })}
          >
            {container.Parts.map((part) => {
              return (
                <K2Part
                  key={part.PartIdentifier}
                  part={part}
                  getPartState={props.getPartState}
                  isChangedParts={props.isChangedParts}
                  toggleMember={props.toggleMember}
                />
              );
            })}
          </div>
        );
      })}
    </>
  );
};

interface PartProps<T extends Part> extends PartBaseProps {
  part: T;
}

const K2Part = (props: PartProps<Part>) => {
  const context = useContext(DashboardContext);
  const vcx = useContext(VCXContext);
  const firstSelectedMember = useRef<VisibleMember>();
  const [zoomed, setZoomed] = useState(false);

  const handleZoom = () => {
    setZoomed(!zoomed);
  };

  const setFirstSelectedMember = (member: VisibleMember) => {
    firstSelectedMember.current = member;
  };

  if ("Description" in props.part) {
    const menuPart = props.part as TilePart;

    return (
      <div
        onClick={(e) => context.partClick(props.part, null, e.shiftKey, e.ctrlKey, null, null, null)}
        className={vcx.renderer.renderRule(tile, {
          vcx: vcx.vcx,
          isMenu: true,
          background: "#" + menuPart.BackgroundColor,
        })}
      >
        <div className={vcx.renderer.renderRule(tileTop, { vcx: vcx.vcx })}>
          <div style={{ flex: "0 0 auto" }}>
            <K2Img glyphId={menuPart.IconPath ? `wui*ARTile_${menuPart.IconPath}` : "ui*element.k2olap"} vcx={vcx.vcx} height={50} width={50} />
          </div>
          <p style={{ fontSize: "18px" }} className={vcx.renderer.renderFontRule(vcx.vcx.DashboardControl.TileFont, vcx.vcx)}>
            {menuPart.TileTitle}
          </p>
        </div>
        <div className={vcx.renderer.renderRule(tileBottom, { vcx: vcx.vcx, background: menuPart.KPIText === "" ? null : "rgb(0 0 0 / 10%)" })}>
          <p className={vcx.renderer.renderFontRule(vcx.vcx.DashboardControl.TileSmallFont, vcx.vcx)}>{menuPart.KPIText}</p>
          {menuPart.KPIStateIcon !== "" ? (
            <div>
              <K2Img
                glyphId={menuPart.KPIStateIcon.includes("false") ? `${menuPart.KPIStateIcon}_invert` : `${menuPart.KPIStateIcon}`}
                vcx={vcx.vcx}
                height={30}
                width={30}
              />
            </div>
          ) : null}
        </div>
      </div>
    );
  } else if ("VisibleMembers" in props.part || "DynamicFilterChoices" in props.part) {
    let content: JSX.Element;
    let part: FilterPart | DynamicFilterPart;
    let partType: string;

    if ("DynamicFilterChoices" in props.part) {
      part = props.part as DynamicFilterPart;
      partType = part.DynamicFilterPartType;

      switch (part.DynamicFilterPartType) {
        case "SingleSelectSlicerDynamicFilter":
          content = (
            <MultiSelectSlicerFilter
              key={part.PartIdentifier}
              filterPart={part}
              setFirstSelectedMember={setFirstSelectedMember}
              firstSelectedMember={firstSelectedMember.current}
              isDynamicPart={true}
            />
          );
          break;
        case "SingleSelectComboDynamicFilter":
          content = <Select filterPart={part} toggleMember={props.toggleMember} isDynamicPart={true} />;
          break;
      }
    } else {
      part = props.part as FilterPart;
      partType = part.FilterPartType;

      switch (part.FilterPartType) {
        case "MultiSelectSlicerFilter":
          content = (
            <MultiSelectSlicerFilter
              key={part.PartIdentifier + Date.now()} // vynucuje re-render kvuli klientske akci (kdyz drzim CTRL)
              filterPart={part}
              setFirstSelectedMember={setFirstSelectedMember}
              firstSelectedMember={firstSelectedMember.current}
              isDynamicPart={false}
            />
          );
          break;
        case "MultiSelectTreeFilter":
        case "MultiSelectComboFilter":
        case "SingleSelectComboFilter":
          content = <Select filterPart={part} toggleMember={props.toggleMember} isDynamicPart={false} />;
          break;
        default:
          break;
      }
    }

    return (
      <div
        style={{
          border: "1px solid #ccc",
          background: "white",
          justifyContent: partType === "MultiSelectSlicerFilter" || partType === "SingleSelectSlicerDynamicFilter" ? "center" : null,
          padding: "5px 10px",
          gap: "5px",
        }}
        className={vcx.renderer.renderRule(layout, {
          vcx: vcx.vcx,
          direction: partType === "MultiSelectSlicerFilter" || partType === "SingleSelectSlicerDynamicFilter" ? "column" : "row",
          basis: part.PartWidth,
        })}
      >
        <div className={vcx.renderer.renderRule(filterName, { vcx: vcx.vcx }) + " " + vcx.renderer.renderFontRule(vcx.vcx.ExpanderControl.HeaderFont, vcx.vcx)}>
          {partType === "SingleSelectSlicerDynamicFilter" ? (part as DynamicFilterPart).DynamicFilterTitle : (part as FilterPart).PartTitle}
        </div>
        <div
          className={vcx.renderer.renderRule(filters, { vcx: vcx.vcx }) + " " + vcx.renderer.renderFontRule(vcx.vcx.DashboardControl.FilterInnerFont, vcx.vcx)}
        >
          {content}
        </div>
      </div>
    );
  } else {
    const chart = (
      <Chart part={props.part as ReportPart} isChangedParts={props.isChangedParts} getPartState={props.getPartState} handleZoom={handleZoom} zoomed={zoomed} />
    );
    return (
      <>
        {chart}
        {zoomed && ReactDOM.createPortal(<div className={vcx.renderer.renderRule(modalBg, { vcx: vcx.vcx })}>{chart}</div>, document.body)}
      </>
    );
  }
};

interface K2ChartProps extends PartProps<ReportPart> {
  zoomed: boolean;
  handleZoom: () => void;
}

const Chart = (props: K2ChartProps) => {
  const context = useContext(DashboardContext);
  const vcx = useContext(VCXContext);
  const menu = useRef<HTMLDivElement>();
  let menuContent: { name: string; type: TARPresentationType }[];
  let content: JSX.Element;
  const [openMenu, setOpenMenu] = useState(false);
  const [x, setX] = useState(0);
  const [y, setY] = useState(0);
  const [chartType, setChartType] = useState(props.part.DefaultPresentation);

  useEffect(() => {
    if (menu.current && window.innerWidth < menu.current.offsetLeft + menu.current.offsetWidth) {
      {
        setX(window.innerWidth - menu.current.offsetWidth);
      }
    }
  }, [openMenu]);

  const getLegendPosition = () => {
    const { glpBottom, glpLeft, glpRight, glpTop } = TARLegendPosition;
    let legendPosition: "bottom" | "center" | "left" | "right" | "top" | "chartArea";

    switch (props.part.LegendPosition) {
      case glpRight:
        legendPosition = "right";
        break;
      case glpLeft:
        legendPosition = "left";
        break;
      case glpBottom:
        legendPosition = "bottom";
        break;
      case glpTop:
        legendPosition = "top";
        break;
      default:
        legendPosition = null;
        break;
    }

    return legendPosition;
  };

  menuContent = [
    { name: "Výchozí nastavení zobrazení", type: props.part.DefaultPresentation },
    { name: "Graf spojnicový", type: TARPresentationType.ptLineChart },
    { name: "Graf sloupcový", type: TARPresentationType.ptBarChart },
    { name: "Graf výsečový", type: TARPresentationType.ptPieChart },
    { name: "Graf skládaný sloupcový", type: TARPresentationType.ptStackedChart },
    { name: "Graf plošný", type: TARPresentationType.ptAreaChart },
    { name: "Tabulka", type: TARPresentationType.ptGrid },
  ];

  const options: ChartOptions = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: getLegendPosition(),
        title: {
          display: true,
          text: props.part.LegendTitle,
        },
      },
      tooltip: {
        backgroundColor: "white",
        borderColor: "#ccc",
        borderWidth: 1,
        cornerRadius: 0,
        bodyColor: "black",
        titleColor: "black",
      },
    },
    interaction: {
      intersect: true,
      mode: "nearest",
    },
    scales: {
      y: {
        grid: {
          borderDash: [10, 5],
        },
      },
      x: {
        ticks: {
          maxRotation: 0,
        },
      },
    },
    onClick: (e: unknown, data: { datasetIndex: number; index: number }[]) => {
      if (data.length > 0) {
        context.partClick(props.part, [], false, false, data[0].datasetIndex, data[0].index, TMouseButton.mbLeft);
      }
    },
  };

  const handleClickZoom = () => {
    props.handleZoom();
  };

  const handleOpenMenu = (e: React.MouseEvent) => {
    setOpenMenu(true);
    setX(e.pageX);
    setY(e.pageY);
  };

  const getChartType = (state: PartState, type: TARPresentationType) => {
    if (state) {
      switch (state.State) {
        case TPartState.psShowPart:
          switch (type) {
            case TARPresentationType.ptChartBySeries:
              return <MixedChart part={props.part} options={options} />;
            case TARPresentationType.ptGrid:
              return <Grid part={props.part} />;
            case TARPresentationType.ptBarChart:
              return <BarChart part={props.part} options={options} />;
            case TARPresentationType.ptLineChart:
              return <LineChart part={props.part} options={options} isAreaChart={false} />;
            case TARPresentationType.ptStackedChart:
              return <BarChart part={props.part} options={options} />;
            case TARPresentationType.ptPieChart:
              return <PieChart part={props.part} options={options} />;
            case TARPresentationType.ptAreaChart:
              return <LineChart part={props.part} options={options} isAreaChart={true} />;
            default:
              break;
          }
          break;
        case TPartState.psShowProgress:
          return <p>{"Načítám"}</p>;
        default:
          // return <p>{`Stav: ${state} `}</p>;
          break;
      }
    }
    return <p>{`Stav: ${state} ${type}`}</p>;
  };

  let state: PartState;

  if (props.getPartState && props.part) state = props.getPartState(props.part.PartIdentifier);

  content = useMemo(() => {
    return getChartType(state, chartType);
  }, [context.dataVersion, chartType]);

  return (
    <div
      style={{
        border: "1px solid #ccc",
        background: "white",
        padding: "5px 10px",
        flex: props.zoomed ? "1 1 auto" : null,
      }}
      className={vcx.renderer.renderRule(layout, { vcx: vcx.vcx, direction: "column", basis: props.part.PartWidth, isChart: true })}
    >
      <div className={vcx.renderer.renderRule(chartHeader, { vcx: vcx.vcx })}>
        <div></div>
        <p className={vcx.renderer.renderFontRule(vcx.vcx.ExpanderControl.HeaderFont, vcx.vcx)}>{props.part.PartTitle}</p>
        <div style={{ gap: "5px" }}>
          <div onClick={handleClickZoom} className="button" style={{ position: "relative" }}>
            <K2Img
              glyphId={props.zoomed ? "wui*zoom.out" : "wui*zoom.in"}
              vcx={vcx.vcx}
              width={20}
              height={20}
              strokeColor={vcx.vcx.getColor(vcx.vcx.Data.ColorMap.BaseColorBck1)}
            />
          </div>
          <div onClick={handleOpenMenu} className="button" style={{ position: "relative" }}>
            <K2Img glyphId={"wui*options"} vcx={vcx.vcx} width={20} height={20} strokeColor={vcx.vcx.getColor(vcx.vcx.Data.ColorMap.BaseColorBck1)} />
          </div>
        </div>
      </div>
      {props.part.Series?.length > 0 ? (
        <div style={{ flex: "1 1 auto", overflow: "hidden" }}>{content}</div>
      ) : (
        <div className={vcx.renderer.renderRule(noData, { vcx: vcx.vcx })}>Žádná data k zobrazení</div>
      )}
      {openMenu &&
        ReactDOM.createPortal(
          <div
            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", alignItems: "flex-start", zIndex: 3 }}
            className={vcx.renderer.renderFontRule(vcx.vcx.LabelControl.Font, vcx.vcx)}
            onClick={() => setOpenMenu(false)}
          >
            <div
              ref={menu}
              style={{
                background: "white",
                border: "1px solid #ccc",
                boxShadow: "3px 3px 7px 0px #00000033",
                flexDirection: "column",
                position: "relative",
                top: y,
                left: x,
              }}
            >
              {menuContent.map((menuItem) => {
                return (
                  <button
                    key={menuItem.name}
                    style={{ padding: "10px 50px", background: "white", border: "none", textAlign: "left" }}
                    className={vcx.renderer.renderFontRule(vcx.vcx.LabelControl.Font, vcx.vcx)}
                    onClick={() => setChartType(menuItem.type)}
                  >
                    {menuItem.name}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

interface ChartProps {
  part: ReportPart;
  options: ChartOptions;
}

const PieChart = (props: ChartProps) => {
  const ref = useRef<ChartJS>();

  useEffect(() => {
    return () => ref.current.destroy();
  }, []);

  return (
    <Pie
      ref={ref}
      options={{
        ...props.options,
        scales: {
          y: {
            display: false,
          },
        },
      }}
      data={{
        labels: props.part.Series[0].DataPoints.map((point) => point.Independent),
        datasets: props.part.Series?.map((serie) => {
          return {
            label: serie.Title,
            data: serie.DataPoints.map((point) => point.Dependent),
            backgroundColor: ["rgb(255, 99, 132)", "rgb(54, 162, 235)", "rgb(255, 205, 86)"],
          };
        }),
      }}
    />
  );
};

const BarChart = (props: ChartProps) => {
  const ref = useRef<ChartJS>();

  useEffect(() => {
    return () => ref.current.destroy();
  }, []);

  return (
    <Bar
      ref={ref}
      options={{
        ...props.options,
        scales: {
          x: {
            ...props.options.scales.x,
            stacked: props.part.DefaultPresentation === TARPresentationType.ptStackedChart ? true : false,
            grid: {
              display: false,
            },
          },
          y: {
            ...props.options.scales.y,
            stacked: props.part.DefaultPresentation === TARPresentationType.ptStackedChart ? true : false,
          },
        },
      }}
      data={{
        labels: props.part.Series[0].DataPoints.map((point) => point.Independent),
        datasets: props.part.Series?.map((serie) => {
          return {
            label: serie.Title,
            data: serie.DataPoints.map((point) => point.Dependent),
            backgroundColor: "#" + serie.Color,
          };
        }),
      }}
    />
  );
};

const LineChart = (props: ChartProps & { isAreaChart: boolean }) => {
  const ref = useRef<ChartJS>();
  let timeout: number;
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    timeout = window.setTimeout(() => {
      setLoaded(true);
    }, 0);

    return () => {
      window.clearInterval(timeout);
      ref.current.destroy();
    };
  }, []);

  const createGradient = (ctx: CanvasRenderingContext2D, area: ChartArea, color: string) => {
    if (!ctx) return;

    const gradient = ctx.createLinearGradient(0, area.bottom, 0, area.top);

    gradient.addColorStop(0, "white");
    gradient.addColorStop(1, color);

    return gradient;
  };

  return (
    <Line
      ref={ref}
      options={{
        ...props.options,
        scales: {
          ...props.options.scales,
          x: {
            ...props.options.scales.x,
            grid: {
              display: false,
            },
          },
        },
      }}
      data={{
        labels: props.part.Series[0].DataPoints.map((point) => point.Independent),
        datasets: props.part.Series?.map((serie) => {
          return {
            label: serie.Title,
            data: serie.DataPoints.map((point) => point.Dependent),
            backgroundColor: props.isAreaChart ? createGradient(ref.current?.ctx, ref.current?.chartArea, "#" + serie.Color + "80") : "#" + serie.Color,
            borderColor: "#" + serie.Color,
            fill: props.isAreaChart ? "start" : null,
            // order: this.props.part.Series.length - index,
          };
        }),
      }}
    />
  );
};

const MixedChart = (props: ChartProps) => {
  const ref = useRef<ChartJS>();

  useEffect(() => {
    return () => ref.current.destroy();
  }, []);

  return (
    <Bar
      ref={ref}
      options={{
        ...props.options,
        scales: {
          x: {
            ...props.options.scales.x,
            grid: {
              display: false,
            },
          },
          y: { ...props.options.scales.y },
        },
      }}
      data={{
        labels: props.part.Series[0].DataPoints.map((point) => point.Independent),
        datasets: props.part.Series?.map((serie) => {
          return {
            label: serie.Title,
            data: serie.DataPoints.map((point) => point.Dependent),
            backgroundColor: "#" + serie.Color,
            borderColor: "#" + serie.Color,
            type: serie.GraphSerieType === TARGraphSerieType.gstLineSerie ? "line" : null,
            order: serie.GraphSerieType === TARGraphSerieType.gstLineSerie ? -1 : 0,
          };
        }),
      }}
    />
  );
};

const table = (props: K2RuleWithVCXProps) => ({
  color: "inherit",
  "& th": {
    color: "white",
    background: "#0054A6",
    textAlign: "left",
    padding: props.vcx.sizeMap(5) + "px",
    fontWeight: "normal",
    width: props.vcx.sizeMap(60) + "px",
  },
  "& td:first-child": {
    color: "white",
    background: "#0054A6",
    paddingRight: props.vcx.sizeMap(20) + "px",
  },
  "& td": {
    padding: props.vcx.sizeMap(5) + "px",
  },
  "& td:not(td:first-child)": {
    textAlign: "right",
  },
  "& tr:nth-child(even)": {
    background: "#FCFCFC",
  },
  "& tr:nth-child(odd)": {
    background: "#F0F0F0",
  },
});

const foreBackColors = (props: K2RuleWithVCXProps & { fore: number; back: number }) => ({
  color: props.fore > -1 ? props.vcx.getColor(props.fore) : null,
  backgroundColor: props.back > -1 ? props.vcx.getColor(props.back) : null,
});

const Grid = (props: Partial<ChartProps>) => {
  const context = useContext(VCXContext);
  const titles = props.part.Series[0].DataPoints.map((point) => point.Independent);

  return (
    <div
      style={{ overflow: "auto", flex: "0 1 auto", flexDirection: "column" }}
      className={context.renderer.renderFontRule(context.vcx.DashboardControl.SimpleTableFont, context.vcx)}
    >
      <table className={context.renderer.renderRule(table, { vcx: context.vcx })}>
        <thead>
          <tr>
            <th></th>
            {titles.map((title, index) => (
              <th key={index}>{title}</th>
            ))}
            {props.part.SeriesRowSum.length > 0 && <th>Celkem</th>}
          </tr>
        </thead>
        <tbody>
          {props.part.Series.map((serie, index) => (
            <tr key={index}>
              <td>{serie.Title}</td>
              {serie.DataPoints.map((point, pointIndex) => (
                <td
                  key={pointIndex}
                  className={context.renderer.renderRule(foreBackColors, { vcx: context.vcx, fore: point.FORE_COLOR, back: point.BACK_COLOR })}
                >
                  {point.FomattedValue === "" ? point.Dependent : point.FomattedValue}
                </td>
              ))}
              {props.part.SeriesRowSum.length > 0 && (
                <td>{props.part.SeriesRowSum[index]?.FomattedValue ? props.part.SeriesRowSum[index]?.FomattedValue : ""}</td>
              )}
            </tr>
          ))}
          {props.part.SeriesColumnSum.length > 0 && (
            <tr>
              <td>Celkem</td>
              {props.part.SeriesColumnSum.map((colSum, index) => (
                <td key={index}>{colSum.FomattedValue}</td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

interface MultiSelectSlicerFilterProps extends Omit<FilterProps, "filterPart"> {
  filterPart: FilterPart | DynamicFilterPart;
  isDynamicPart: boolean;
}

const MultiSelectSlicerFilter = (props: MultiSelectSlicerFilterProps) => {
  const context = useContext(DashboardContext);
  let selectedMembers: VisibleMember[] = [];

  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (e.key !== "Control") return;

    context.partClick(props.filterPart, selectedMembers, e.shiftKey, e.ctrlKey, null, null, null);
  };

  useEffect(() => {
    if (!props.isDynamicPart) {
      selectedMembers = (props.filterPart as FilterPart).VisibleMembers.filter((val) => val.Selected === true);
    }
  }, []);

  const handleClick = (e: React.MouseEvent, member: VisibleMember | DynamicFilterChoice) => {
    if (!props.isDynamicPart) {
      const part = props.filterPart as FilterPart;
      const visibleMember = member as VisibleMember;
      if (!(context.control as NclDashboard).isPartsUpdateCompleted()) return;

      if (!e.ctrlKey && !e.shiftKey) {
        if (Context.DeviceInfo.IsTouchDevice) {
          if (visibleMember.Selected) {
            selectedMembers = selectedMembers.filter((selectedMember) => selectedMember.Identifier !== visibleMember.Identifier);
          } else {
            selectedMembers.push(visibleMember);
          }
        } else {
          selectedMembers = [];
          selectedMembers.push(visibleMember);
          props.setFirstSelectedMember(visibleMember);
        }
      } else if (e.shiftKey) {
        if (!props.firstSelectedMember) return;

        selectedMembers = [];
        let first = part.VisibleMembers.findIndex((val) => val.Identifier === props.firstSelectedMember.Identifier);
        let last = part.VisibleMembers.findIndex((val) => val.Identifier === visibleMember.Identifier);

        if (first > last) {
          const temp = first;
          first = last;
          last = temp;
        }

        selectedMembers = part.VisibleMembers.filter((val, i) => {
          if (i >= first && i <= last) {
            return val;
          }
        });
      } else if (e.ctrlKey) {
        selectedMembers.push(visibleMember);
      }

      if (e.ctrlKey) return;

      context.partClick(props.filterPart, selectedMembers, e.shiftKey, e.ctrlKey, null, null, null);
    } else {
      const visibleMember = member as DynamicFilterChoice;
      (context.control as NclDashboard).dynamicFilterPartClick(props.filterPart.PartIdentifier, visibleMember.DynamicFilterChoiceItemID);
    }
  };

  if (!props.isDynamicPart) {
    const part = props.filterPart as FilterPart;

    return (
      <>
        {part.VisibleMembers?.map((member) => {
          return (
            <MultiSelectSlicerFilterButton
              key={member.Identifier}
              handleKeyUp={handleKeyUp}
              handleClick={handleClick}
              member={member}
              type={part.FilterPartType}
            />
          );
        })}
      </>
    );
  } else {
    const part = props.filterPart as DynamicFilterPart;

    return (
      <>
        {part.DynamicFilterChoices?.map((member) => {
          return (
            <MultiSelectSlicerFilterButton
              key={member.DynamicFilterChoiceItemDescription}
              handleKeyUp={handleKeyUp}
              handleClick={handleClick}
              member={member}
              type={part.DynamicFilterPartType}
              selected={part.DynamicFilterSelectedChoiceIndex}
            />
          );
        })}
      </>
    );
  }
};

interface MultiSelectSlicerFilterButtonProps {
  member: VisibleMember | DynamicFilterChoice;
  type: string;
  selected?: number;
  handleKeyUp: (e: React.KeyboardEvent) => void;
  handleClick: (e: React.MouseEvent, member: VisibleMember | DynamicFilterChoice) => void;
}

const MultiSelectSlicerFilterButton = (props: MultiSelectSlicerFilterButtonProps) => {
  const vcx = useContext(VCXContext);
  const [selected, setSelected] = useState(false);

  useEffect(() => {
    setSelected((props.member as VisibleMember).Selected);
  }, []);

  const handleClick = (e: React.MouseEvent) => {
    if (props.type === "MultiSelectSlicerFilter") {
      if (!e.ctrlKey) {
        props.handleClick(e, props.member as VisibleMember);
        return;
      }

      setSelected(true);
      props.handleClick(e, props.member as VisibleMember);
    } else {
      props.handleClick(e, props.member as DynamicFilterChoice);
    }
  };

  if (props.type === "MultiSelectSlicerFilter") {
    const member = props.member as VisibleMember;

    return (
      <button
        key={member.Identifier}
        className={vcx.renderer.renderRule(buttonRule, { vcx: vcx.vcx, isSelected: selected })}
        onClick={handleClick}
        onKeyUp={(e) => props.handleKeyUp(e)}
      >
        {member.Caption}
      </button>
    );
  } else {
    const member = props.member as DynamicFilterChoice;

    return (
      <button
        key={member.DynamicFilterChoiceItemDescription}
        className={vcx.renderer.renderRule(buttonRule, { vcx: vcx.vcx, isSelected: props.selected === member.DynamicFilterChoiceItemID })}
        onClick={handleClick}
      >
        {member.DynamicFilterChoiceItemDescription}
      </button>
    );
  }
};

interface FlatMember {
  memberId: string;
  memberState: TARMemberState;
  parentId: string;
  caption: string;
}

const MultiSelectTreeFilter = (props: FilterProps) => {
  const context = useContext(DashboardContext);
  const prevDataVersion = useRef<number>(null);
  const [showPage, setShowPage] = useState(1);
  const [updatedMembers, setUpdatedMembers] = useState<FlatMember[]>([]);

  const actualFlattenTree = (treeMembers: VisibleMember[], flatMembers: FlatMember[]) => {
    treeMembers.map((member) => {
      flatMembers.push({ memberId: member.Identifier, memberState: member.MemberState, parentId: member.Parent?.Identifier, caption: member.Caption });

      if (member.Children) {
        actualFlattenTree(member.Children, flatMembers);
      }
    });
  };

  /**
   * Converts nested array of objects to flat array.
   * @param treeMembers Array of nested objects.
   * @returns New flat array.
   */
  const flattenTree = (treeMembers: VisibleMember[]) => {
    const flatMembers: FlatMember[] = [];
    actualFlattenTree(treeMembers, flatMembers);

    return flatMembers;
  };

  /**
   * Updates current member state with data from server.
   * @param flatMembers Flatten members.
   * @returns New array of members with updated state.
   */
  const synchronizeTreeState = (flatMembers: FlatMember[], updatedMembers: FlatMember[]) => {
    const flatMembersCopy: FlatMember[] = flatMembers.map((flatMember) => ({ ...flatMember }));

    updatedMembers.map((member) => {
      flatMembersCopy.find((flatMember) => {
        if (flatMember.memberId === member.memberId) {
          flatMember.memberState = member.memberState;

          return true;
        }
      });
    });

    return flatMembersCopy;
  };

  useEffect(() => {
    if (context.dataVersion !== prevDataVersion.current) {
      const flatMembers = flattenTree(props.filterPart.VisibleMembers);
      const synchronizedMembers = synchronizeTreeState(flatMembers, updatedMembers);
      setUpdatedMembers(synchronizedMembers);
      prevDataVersion.current = context.dataVersion;
      props.setTreeFilterMembers(synchronizedMembers);
    }
  }, [context.dataVersion]);

  useEffect(() => {
    if (props.checkAll === MultiSelectComboOperation.UncheckAll) {
      const members = updatedMembers.map((member) => ({ ...member }));
      members.map((member) => (member.memberState = 0));
      setUpdatedMembers(members);
      props.setSelectedMembers(MultiSelectComboOperation.Default, null);
      props.setTreeFilterMembers(members);
    }
  }, [props.checkAll]);

  const updateMembersInputState = (currentMember: VisibleMember, memberState: number) => {
    const members = updatedMembers.map((member) => ({ ...member }));
    const member = members.find((member) => member.memberId === currentMember.Identifier);

    if (memberState === TARMemberState.msClear) {
      member.memberState = TARMemberState.msChecked;
    } else if (memberState === TARMemberState.msChecked) {
      if (currentMember.Children) {
        const children = members.filter((newMember) => newMember.parentId === member.memberId);
        const some = children.some((child) => child.memberState === TARMemberState.msChecked || child.memberState === TARMemberState.msGray);

        if (some) {
          member.memberState = TARMemberState.msGray;
        } else {
          member.memberState = TARMemberState.msClear;
        }
      } else {
        member.memberState = TARMemberState.msClear;
      }
    } else {
      member.memberState = TARMemberState.msChecked;
    }

    const parent = members.find((item) => item.memberId === member.parentId);

    if (parent) {
      updateParentsInputState(parent, members);
    }

    props.setTreeFilterMembers(members);
    setUpdatedMembers(members);
  };

  const updateParentsInputState = (parent: FlatMember, members: FlatMember[]) => {
    const children = members.filter((member) => member.parentId === parent.memberId);
    const some = children.some((child) => child.memberState === TARMemberState.msChecked || child.memberState === TARMemberState.msGray);

    if (some) {
      members.find((member) => {
        if (member.memberId === parent.memberId) {
          if (member.memberState === TARMemberState.msChecked) {
            member.memberState = TARMemberState.msChecked;
          } else {
            member.memberState = TARMemberState.msGray;
          }

          return true;
        }
      });
    } else {
      members.find((member) => {
        if (member.memberId === parent.memberId) {
          if (parent.memberState !== TARMemberState.msChecked) {
            member.memberState = TARMemberState.msClear;
          }

          return true;
        }
      });
    }

    if (parent.parentId) {
      const newParent = members.find((member) => parent.parentId === member.memberId);
      updateParentsInputState(newParent, members);
    }
  };

  const renderRow = (member: VisibleMember, depth: number): JSX.Element => {
    let children = null;

    if (member.Children?.length > 0) {
      children = member.Children.map((member: any) => {
        const newDepth = depth + 1;

        return renderRow(member, newDepth);
      });
    }

    const flatMember = updatedMembers.find((flatMember) => flatMember.memberId === member.Identifier);

    if (!flatMember) return null;

    return (
      <MultiSelectTreeFilterMember
        key={member.Identifier}
        member={member}
        depth={depth}
        partId={props.filterPart.PartIdentifier}
        expandedMembers={props.expandedMembers}
        toggleMember={props.toggleMember}
        setExpandedMembers={props.setExpandedMembers}
        part={props.filterPart}
        updateMembers={updateMembersInputState}
        memberState={flatMember?.memberState}
      >
        <>{children}</>
      </MultiSelectTreeFilterMember>
    );
  };

  if (updatedMembers.length === 0) return null;

  return (
    <>
      {false && (
        <>
          <div style={{ flex: "0 0 auto" }}>
            <form>
              <input></input>
              <button>Hledej</button>
            </form>
          </div>
          <div style={{ flex: "0 0 auto" }}>
            <div style={{ background: showPage === 1 ? "white" : null }} onClick={() => setShowPage(1)}>
              Všechny prvky
            </div>
            <div style={{ background: showPage === 2 ? "white" : null }} onClick={() => setShowPage(2)}>
              Nalezené prvky
            </div>
          </div>
        </>
      )}
      {showPage === 1 && (
        <div style={{ background: "white", flexDirection: "column", flex: "1 1 auto", overflow: "auto" }}>
          <table>
            <tbody>{props.filterPart.VisibleMembers.map((member) => renderRow(member, 0))}</tbody>
          </table>
        </div>
      )}
      {showPage === 2 && (
        <div style={{ background: "white", flexDirection: "column", flex: "1 1 auto" }}>
          <table>
            <thead>
              <tr>
                <th>Jméno</th>
                <th>Cesta</th>
              </tr>
            </thead>
          </table>
        </div>
      )}
    </>
  );
};

interface MultiSelectTreeFilterMemberProps {
  member: VisibleMember;
  depth: number;
  children: JSX.Element;
  partId: string;
  expandedMembers: VisibleMember[];
  part: FilterPart;
  memberState: TARMemberState;
  toggleMember: (partId: string, memberId: string) => void;
  setExpandedMembers: (expanding: boolean, member: VisibleMember) => void;
  updateMembers: (members: VisibleMember, memberState: number) => void;
}

const MultiSelectTreeFilterMember = (props: MultiSelectTreeFilterMemberProps) => {
  const [expanded, setExpanded] = useState(false);
  const context = useContext(DashboardContext);
  const inputRef = useRef<HTMLInputElement>();

  useEffect(() => {
    (context.control as NclDashboard).ExpandedMembers.map((expandedMember) => {
      if (expandedMember.Identifier === props.member.Identifier) {
        setExpanded(true);
      }
    });

    if (props.memberState === TARMemberState.msClear) {
      inputRef.current.checked = false;
      inputRef.current.indeterminate = false;
    } else if (props.memberState === TARMemberState.msChecked) {
      inputRef.current.indeterminate = false;
      inputRef.current.checked = true;
    } else {
      inputRef.current.checked = false;
      inputRef.current.indeterminate = true;
    }
  }, [props.memberState]);

  const expandOrCollapse = (member: VisibleMember) => {
    if (expanded) {
      props.setExpandedMembers(expanded, member);
      setExpanded(false);
    } else {
      props.setExpandedMembers(expanded, member);
      if (!member.Children) {
        props.toggleMember(props.partId, member.Identifier);
      }
      setExpanded(true);
    }
  };

  const updateSelect = () => {
    props.updateMembers(props.member, props.memberState);
  };

  return (
    <>
      <tr>
        <td>
          <div style={{ display: "flex", alignItems: "center" }}>
            <span style={{ paddingLeft: `${15 * props.depth}px` }}></span>
            {props.member.Children?.length !== 0 ? (
              (context.control as NclDashboard).ExpandedMembers.find((expandedMember) => expandedMember.Identifier === props.member.Identifier) ? (
                <div style={{ width: "10px" }} onClick={() => expandOrCollapse(props.member)}>
                  -
                </div>
              ) : (
                <div style={{ width: "10px" }} onClick={() => expandOrCollapse(props.member)}>
                  +
                </div>
              )
            ) : (
              <div style={{ width: "10px" }}></div>
            )}
            <div style={{ padding: "5px" }}>
              <input type="checkbox" onChange={updateSelect} ref={inputRef}></input>
            </div>
            <p onDoubleClick={() => expandOrCollapse(props.member)}>{props.member.Caption}</p>
          </div>
        </td>
      </tr>
      {expanded && props.children}
    </>
  );
};

interface FilterProps {
  filterPart: FilterPart;
  checkAll?: MultiSelectComboOperation;
  firstSelectedMember?: VisibleMember;
  expandedMembers?: VisibleMember[];
  setSelectedMembers?: (operation: MultiSelectComboOperation, member: VisibleMember) => void;
  closeModal?: (e: React.MouseEvent) => void;
  setFirstSelectedMember?: (member: VisibleMember) => void;
  toggleMember?: (partId: string, memberId: string) => void;
  setExpandedMembers?: (expanding: boolean, member: VisibleMember) => void;
  setTreeFilterMembers?: (members: FlatMember[]) => void;
}

const MultiSelectComboFilter = (props: FilterProps) => {
  return (
    <table>
      <tbody>
        {props.filterPart.VisibleMembers.map((member) => {
          return <MultiSelectComboFilterRow key={member.Identifier} member={member} setSelectedMembers={props.setSelectedMembers} checkAll={props.checkAll} />;
        })}
      </tbody>
    </table>
  );
};

interface MultiSelectComboFilterRowProps {
  member: VisibleMember;
  checkAll: MultiSelectComboOperation;
  setSelectedMembers: (operation: MultiSelectComboOperation, member: VisibleMember) => void;
}

const MultiSelectComboFilterRow = (props: MultiSelectComboFilterRowProps) => {
  const [selected, setSelected] = useState(props.member.Selected); // jen pro nastaveni defaultni hodnoty, jinak se jedna o anti-pattern, viz https://reactjs.org/docs/react-component.html#constructor

  const handleClick = (member: VisibleMember, checked: boolean) => {
    setSelected(checked);

    if (!checked) {
      props.setSelectedMembers(MultiSelectComboOperation.Uncheck, member);
    } else {
      props.setSelectedMembers(MultiSelectComboOperation.Check, member);
    }
  };

  useEffect(() => {
    if (props.checkAll === MultiSelectComboOperation.CheckAll) {
      setSelected(true);
    } else if (props.checkAll === MultiSelectComboOperation.UncheckAll) {
      setSelected(false);
    }
  }, [props.checkAll]);

  return (
    <tr>
      <td>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ padding: "5px" }}>
            <input id={props.member.Identifier} type="checkbox" onChange={(e) => handleClick(props.member, e.currentTarget.checked)} checked={selected}></input>
          </span>
          <label htmlFor={props.member.Identifier}>{props.member.Caption}</label>
        </div>
      </td>
    </tr>
  );
};

interface SingleSelectComboFilterProps extends Omit<FilterProps, "filterPart"> {
  filterPart: FilterPart | DynamicFilterPart;
  isDynamicPart: boolean;
}

const SingleSelectComboFilter = (props: SingleSelectComboFilterProps) => {
  let content: JSX.Element[];

  if (!props.isDynamicPart) {
    content = (props.filterPart as FilterPart).VisibleMembers.map((member) => {
      return (
        <SingleSelectComboFilterRow
          member={member}
          key={member.Identifier}
          closeModal={props.closeModal}
          setSelectedMembers={props.setSelectedMembers}
          isDynamicPart={false}
        />
      );
    });
  } else {
    content = (props.filterPart as DynamicFilterPart).DynamicFilterChoices.map((member) => {
      return (
        <SingleSelectComboFilterRow
          member={member}
          key={member.DynamicFilterChoiceItemDescription}
          closeModal={props.closeModal}
          setSelectedMembers={props.setSelectedMembers}
          isDynamicPart={true}
          selected={member.DynamicFilterChoiceItemID === (props.filterPart as DynamicFilterPart).DynamicFilterSelectedChoiceIndex}
        />
      );
    });
  }

  return <>{content}</>;
};

interface RowProps {
  isDynamicPart: boolean;
  member: VisibleMember | DynamicFilterChoice;
  selected?: boolean;
  closeModal: (e: React.MouseEvent) => void;
  setSelectedMembers: (operation: MultiSelectComboOperation, member: VisibleMember) => void;
}

const SingleSelectComboFilterRow = (props: RowProps) => {
  let selected: boolean;

  if (!props.isDynamicPart) {
    selected = (props.member as VisibleMember).Selected;
  } else {
    selected = props.selected;
  }

  const handleMemberClick = (e: React.MouseEvent, member: VisibleMember) => {
    props.setSelectedMembers(MultiSelectComboOperation.Check, member);
    props.closeModal(e);
  };

  return (
    <div style={{ background: selected ? "#c9eeff" : null, flex: "0 0 auto", padding: "3px 5px" }} onClick={(e) => handleMemberClick(e, props.member as any)}>
      {props.isDynamicPart ? (props.member as DynamicFilterChoice).DynamicFilterChoiceItemDescription : (props.member as VisibleMember).Caption}
    </div>
  );
};

interface SelectProps {
  filterPart: FilterPart | DynamicFilterPart;
  isDynamicPart: boolean;
  toggleMember?: (partId: string, memberId: string) => void;
}

enum MultiSelectComboOperation {
  Check,
  Uncheck,
  CheckAll,
  UncheckAll,
  Default,
}

const Select = (props: SelectProps) => {
  const context = useContext(DashboardContext);
  const vcx = useContext(VCXContext);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0, width: 0 });
  const [showModal, setShowModal] = useState(false);
  const [checkAll, setCheckAll] = useState(MultiSelectComboOperation.Default);
  const [selectText, setSelectText] = useState("");
  const selectedMembers = useRef<VisibleMember[]>([]);
  const expandedMembers = useRef<VisibleMember[]>([]);
  const treeFilterMembers = useRef<{ memberId: string; state: number; caption: string }[]>([]);
  let partType: string;

  useEffect(() => {
    let text = "";

    if (!props.isDynamicPart) {
      (props.filterPart as FilterPart).VisibleMembers.map((member) => {
        if (member.Selected) {
          text += member.Caption + ", ";
        }
      });

      text = text.slice(0, -2);
    } else {
      text = (props.filterPart as DynamicFilterPart).DynamicFilterChoices.find(
        (item) => item.DynamicFilterChoiceItemID === (props.filterPart as DynamicFilterPart).DynamicFilterSelectedChoiceIndex
      ).DynamicFilterChoiceItemDescription;
    }

    setSelectText(text);
  }, []);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (partType !== "MultiSelectTreeFilter") {
      if (!props.isDynamicPart) {
        selectedMembers.current = (props.filterPart as FilterPart).VisibleMembers.filter((member) => {
          if (member.Selected) return member;
        });
      } else {
        selectedMembers.current = (props.filterPart as DynamicFilterPart).DynamicFilterChoices.filter((member) => {
          if (member.DynamicFilterChoiceItemID === (props.filterPart as DynamicFilterPart).DynamicFilterSelectedChoiceIndex) return member;
        }) as any;
      }
    }

    const rect = e.currentTarget.getBoundingClientRect();
    setModalPosition({ top: e.currentTarget.offsetTop + e.currentTarget.offsetHeight, left: e.currentTarget.offsetLeft, width: rect.width });
    setShowModal(true);
  };

  const closeModal = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (partType === "MultiSelectTreeFilter") {
      (context.control as NclDashboard).updateSelectedMembersInTreeFilter(props.filterPart.PartIdentifier, treeFilterMembers.current);
    } else if (partType === "SingleSelectComboDynamicFilter") {
      const choices = selectedMembers.current as unknown[];

      (context.control as NclDashboard).dynamicFilterPartClick(
        props.filterPart.PartIdentifier,
        (choices as DynamicFilterChoice[])[0].DynamicFilterChoiceItemID
      );
    } else {
      context.partClick(props.filterPart, selectedMembers.current, false, false, null, null, null);
    }

    let text = "";

    if (partType === "MultiSelectTreeFilter") {
      treeFilterMembers.current.map((member) => {
        if (member.state === TARMemberState.msChecked) text += member.caption + ", ";
      });
    } else {
      if (!props.isDynamicPart) {
        selectedMembers.current.map((member) => {
          text += member.Caption + ", ";
        });
      } else {
        const choices = selectedMembers.current as unknown[];

        (choices as DynamicFilterChoice[]).map((item) => {
          text += item.DynamicFilterChoiceItemDescription + ", ";
        });
      }
    }

    text = text.slice(0, -2);

    setShowModal(false);
    setSelectText(text);
  };

  const setSelectedMembers = (operation: MultiSelectComboOperation, updatedMember: VisibleMember) => {
    switch (operation) {
      case MultiSelectComboOperation.Check:
        if (partType === "SingleSelectComboFilter" || partType === "SingleSelectComboDynamicFilter") {
          selectedMembers.current = [];
        }

        selectedMembers.current.push(updatedMember);
        break;
      case MultiSelectComboOperation.Uncheck:
        selectedMembers.current = selectedMembers.current.filter((member) => member.Identifier !== updatedMember.Identifier);
        break;
      case MultiSelectComboOperation.CheckAll:
        selectedMembers.current = (props.filterPart as FilterPart).VisibleMembers;
        setCheckAll(MultiSelectComboOperation.CheckAll);
        break;
      case MultiSelectComboOperation.UncheckAll:
        selectedMembers.current = [];
        setCheckAll(MultiSelectComboOperation.UncheckAll);
        break;
      default:
        setCheckAll(MultiSelectComboOperation.Default);
        break;
    }
  };

  const setExpandedMembers = (expanded: boolean, member: VisibleMember) => {
    if (expanded) {
      expandedMembers.current = expandedMembers.current.filter((expandedMember) => expandedMember.Identifier !== member.Identifier);
    } else {
      expandedMembers.current.push(member);
    }

    (context.control as NclDashboard).ExpandedMembers = expandedMembers.current;
  };

  const setTreeFilterMembers = (members: FlatMember[]) => {
    const checkedOrGray = members.map((member) => {
      if (member.memberState === TARMemberState.msChecked || member.memberState === TARMemberState.msGray) {
        return { memberId: member.memberId, state: member.memberState, caption: member.caption };
      }
    });

    treeFilterMembers.current = checkedOrGray.filter((member) => member != null);
  };

  let filter: JSX.Element;

  if (!props.isDynamicPart) {
    const part = props.filterPart as FilterPart;
    partType = part.FilterPartType;

    if (part.FilterPartType === "MultiSelectComboFilter") {
      filter = <MultiSelectComboFilter filterPart={part} setSelectedMembers={setSelectedMembers} checkAll={checkAll} />;
    } else if (part.FilterPartType === "SingleSelectComboFilter") {
      filter = <SingleSelectComboFilter filterPart={part} closeModal={closeModal} setSelectedMembers={setSelectedMembers} isDynamicPart={false} />;
    } else {
      filter = (
        <MultiSelectTreeFilter
          filterPart={part}
          expandedMembers={expandedMembers.current}
          toggleMember={props.toggleMember}
          setExpandedMembers={setExpandedMembers}
          setTreeFilterMembers={setTreeFilterMembers}
          checkAll={checkAll}
          setSelectedMembers={setSelectedMembers}
        />
      );
    }
  } else {
    const part = props.filterPart as DynamicFilterPart;
    partType = part.DynamicFilterPartType;

    filter = <SingleSelectComboFilter filterPart={part} closeModal={closeModal} setSelectedMembers={setSelectedMembers} isDynamicPart={true} />;
  }

  return (
    <div onClick={handleClick} className={vcx.renderer.renderRule(select, { vcx: vcx.vcx })} tabIndex={1}>
      <div className={vcx.renderer.renderRule(selectMembers, { vcx: vcx.vcx })}>
        <p style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{selectText}</p>
      </div>
      <div style={{ flex: "0 0 auto" }}>
        <K2Img
          glyphId={partType === "MultiSelectTreeFilter" ? "wui*filter" : "wui*lookupsimple"}
          vcx={vcx.vcx}
          width={20}
          height={20}
          strokeColor={vcx.vcx.getColor(vcx.vcx.Data.ColorMap.BaseColorBck1)}
        />
      </div>
      {showModal && (
        <Modal
          closeModal={closeModal}
          showCheckCheckbox={partType === "MultiSelectComboFilter" || partType === "MultiSelectTreeFilter"}
          showUncheckCheckbox={partType === "MultiSelectComboFilter"}
          coords={{ top: modalPosition.top, left: modalPosition.left, width: modalPosition.width }}
          setSelectedMembers={setSelectedMembers}
        >
          {filter}
        </Modal>
      )}
    </div>
  );
};

interface ModalProps {
  children: JSX.Element;
  coords: { left: number; top: number; width: number };
  showCheckCheckbox: boolean;
  showUncheckCheckbox: boolean;
  setSelectedMembers: (operation: MultiSelectComboOperation, member: VisibleMember) => void;
  closeModal: (e: React.MouseEvent<Element, MouseEvent>) => void;
}

const Modal = (props: ModalProps) => {
  const vcx = useContext(VCXContext);

  const checkOrUncheckAll = (checked: boolean) => {
    if (checked) {
      props.setSelectedMembers(MultiSelectComboOperation.CheckAll, null);
    } else {
      props.setSelectedMembers(MultiSelectComboOperation.UncheckAll, null);
    }
  };

  return (
    <div className={vcx.renderer.renderRule(selectBg, { vcx: vcx.vcx })} onClick={(e) => props.closeModal(e)}>
      <div
        className={vcx.renderer.renderRule(selectModal, {
          vcx: vcx.vcx,
          top: props.coords.top,
          width: props.coords.width,
          left: props.coords.left,
        })}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ overflow: "auto", flexDirection: "column" }}>{props.children}</div>
        <div style={{ flex: "0 0 auto", gap: "5px" }}>
          {props.showCheckCheckbox && <Checkbox checked={false} checkOrUncheckAll={checkOrUncheckAll} />}
          {props.showUncheckCheckbox && <Checkbox checked={true} checkOrUncheckAll={checkOrUncheckAll} />}
          <div style={{ flex: "1 1 auto" }}>
            <button style={{ width: "100%", padding: "3px", font: "inherit" }} onClick={(e) => props.closeModal(e)}>
              Zavřít
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface CheckboxProps {
  checked: boolean;
  checkOrUncheckAll: (checked: boolean) => void;
}

const Checkbox = (props: CheckboxProps) => (
  <div style={{ alignItems: "center" }} onClick={() => props.checkOrUncheckAll(props.checked)}>
    <K2Img glyphId={props.checked ? "wui*checkbox" : "wui*uncheckbox"} vcx={null} width={15} height={15} />
  </div>
);
